// External Imports
import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MdAdd, MdRefresh } from "react-icons/md";
import { BiSolidMegaphone } from "react-icons/bi";

// Internal Imports
import Heading from "../UI/Heading";
import SearchBar from "../UI/SearchBar";
import PartnerCard from "./PartnerCard";
import { UtilityContext } from "../../context/util-context";
import { requestAdminAPI } from "../../api/posh-api";
import PaginationMenu from "../UI/PaginationMenu";
import LoadingCard from "../UI/LoadingCard";
import NoRecordsFound from "../UI/NoRecordsFound";
import ExportCsvButton from "../UI/ExportCsvButton";
import FilterMenu from "../UI/FilterMenu";
import DateFilter from "../UI/DateFilter";

export default function Partners() {
  const { navigate } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Default Filters
  const defaultFilters = {
    query: "",
    sd: "",
    ed: "",
    type: "all",
    applications: "all",
    limit: 50,
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key]),
  );

  // Initialize States
  const [partners, setPartners] = useState([]);
  const [filter, setFilter] = useState(paramFilters);
  const [loading, setLoading] = useState(true);

  /* ======================= FETCH RECORDS ======================= */
  const fetchRecords = async () => {
    try {
      setLoading(true);

      // Send Request
      const data = await requestAdminAPI("search", {
        table: "partner",
        limit: filter.limit,
        filters: filter,
      });

      // Replace URL
      let url = "/partners?";
      Object.keys(filter).forEach((key) => {
        url += `${key}=${filter[key] ?? defaultFilters[key]}&`;
      });
      navigate(url, {}, { replace: true });

      setPartners(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Filter Options
  const filterOptions = [
    {
      label: "Type",
      options: [
        { label: "All", value: "all" },
        { label: "Individual", value: "individual" },
        { label: "Business", value: "business" },
      ],
      selected: filter.type,
    },
    {
      label: "Applications",
      options: [
        { label: "All", value: "all" },
        { label: "Exist", value: "any" },
        { label: "None", value: "none" },
      ],
      selected: filter.applications,
    },
  ];

  // Apply Filters
  useEffect(() => {
    fetchRecords();
  }, [filter]);

  return (
    <div className="container">
      <Heading title="Partners" isBackArrow={true}>
        <DateFilter
          startDate={filter.sd}
          endDate={filter.ed}
          onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
        />
        <FilterMenu
          options={filterOptions}
          onSubmit={(f) => setFilter({ ...filter, ...f })}
        />
        <MdAdd
          onClick={() => navigate("/partners/add")}
          className="text-3xl icon"
        />
        <BiSolidMegaphone
          onClick={() => navigate(`/announcements/add${search}table=partner`)}
          className="text-2xl icon"
        />
        <MdRefresh
          onClick={fetchRecords}
          className="text-2xl text-white cursor-pointer hover:text-stone-400"
        />
        <ExportCsvButton array={partners} filename="partners" />
      </Heading>

      <SearchBar
        searchHandler={(query) => setFilter({ ...filter, query })}
        clearSearch={() => setFilter(defaultFilters)}
        searchValue={filter.query}
      />

      {loading ? (
        <LoadingCard columns={5} max={6} />
      ) : partners.length > 0 ? (
        <div className="list">
          {partners.map((partner) => (
            <PartnerCard partner={partner} key={partner.id} />
          ))}
        </div>
      ) : (
        <NoRecordsFound />
      )}

      <PaginationMenu
        limit={filter.limit}
        setLimit={(l) => setFilter({ ...filter, limit: l })}
        loading={loading}
        count={partners.length}
      />
    </div>
  );
}
